import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, BadgeCheck, Gamepad2, Store, MessageCircle, Phone, ChevronDown, ArrowRight, HelpCircle } from 'lucide-react';
import { CallbackButton, SAC_PHONE_DISPLAY, SAC_PHONE_TEL } from './CallbackModal';

const SITE = 'https://fernandotech.com.br';
const WA = `https://wa.me/5516991378312?text=${encodeURIComponent('Olá! Quero comprar equipamentos de TI com a Fernando Tech. Pode me ajudar?')}`;

const OPTIONS = [
  { to: '/loja-daten', icon: BadgeCheck, title: 'Loja Daten', tag: 'Empresas · Escolas · Setor público', desc: 'Notebooks, desktops, mini PCs e all in one Daten com revenda, afiliado e assistência técnica autorizada.', color: 'blue' },
  { to: '/navegamer', icon: Gamepad2, title: 'Navegamer', tag: 'Gamers · Criadores · Profissionais', desc: 'PCs gamer, notebooks NAVE com RTX e periféricos SteelSeries, com montagem, upgrade e suporte local.', color: 'fuchsia' },
  { href: 'https://loja.fernandotech.com.br/', icon: Store, title: 'Loja Fernando Tech', tag: 'Peças · Acessórios · Licenças', desc: 'Nossa loja virtual com componentes, acessórios, câmeras Intelbras e licenças de software.', color: 'cyan' },
];

const COLORS = {
  blue:    { border: 'hover:border-blue-500/60',    bg: 'bg-blue-500/10 border-blue-500/20',       ic: 'text-blue-300' },
  fuchsia: { border: 'hover:border-fuchsia-500/60', bg: 'bg-fuchsia-500/10 border-fuchsia-500/20', ic: 'text-fuchsia-300' },
  cyan:    { border: 'hover:border-cyan-500/60',    bg: 'bg-cyan-500/10 border-cyan-500/20',       ic: 'text-cyan-300' },
};

const FAQ = [
  { q: 'Por que comprar equipamentos de TI com a Fernando Tech?', a: 'Porque você compra com quem vai dar suporte depois. Somos revenda e assistência autorizada Daten, afiliado oficial Navegamer e temos 17+ anos de experiência em TI em Ribeirão Preto. Indicamos a configuração certa, configuramos, instalamos e damos garantia.' },
  { q: 'Vocês atendem fora de Ribeirão Preto?', a: 'Sim. Os equipamentos são entregues em todo o Brasil e o suporte remoto também é nacional. Em Ribeirão Preto e região fazemos atendimento presencial.' },
  { q: 'Como funciona o "Me ligue agora"?', a: 'Você informa seu nome e o produto ou serviço de interesse. A Fernando Tech entra em contato com você por telefone (16) 3237-1003 ou WhatsApp. Você não precisa ligar — nós ligamos.' },
  { q: 'Posso pagar parcelado?', a: 'Sim. Nas lojas Daten e Navegamer há parcelamento no cartão e desconto no PIX. Na Loja Fernando Tech aceitamos PIX, cartão e boleto.' },
];

const LeadCapture = () => {
  const [open, setOpen] = useState(0);

  useEffect(() => {
    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ItemList',
          '@id': `${SITE}/#lojas-parceiras`,
          name: 'Onde comprar equipamentos de TI com a Fernando Tech',
          itemListElement: OPTIONS.map((o, i) => ({ '@type': 'ListItem', position: i + 1, name: o.title, url: o.to ? `${SITE}${o.to}` : o.href })),
        },
        {
          '@type': 'FAQPage',
          '@id': `${SITE}/#lojas-faq`,
          mainEntity: FAQ.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ],
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.id = 'ld-lead-capture';
    s.text = JSON.stringify(ld);
    document.getElementById('ld-lead-capture')?.remove();
    document.head.appendChild(s);
    return () => document.getElementById('ld-lead-capture')?.remove();
  }, []);

  return (
    <section id="comprar-ti" className="py-20 bg-slate-950 border-t border-slate-800/60 relative overflow-hidden" style={{ scrollMarginTop: '100px' }} data-testid="lead-capture-section" aria-labelledby="comprar-ti-title">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[28rem] h-72 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-5 gap-10 items-start mb-12">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold mb-4">
              <ShoppingBag className="w-4 h-4" /> Compre com quem entende de TI
            </div>
            <h2 id="comprar-ti-title" className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight">
              Notebooks, PCs gamer e equipamentos de TI <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">com suporte técnico de verdade</span>
            </h2>
            <p className="text-slate-400 leading-relaxed mb-4">
              Comprar tecnologia em loja online é fácil; difícil é ter alguém para configurar, instalar e resolver quando algo dá errado.
              A <strong className="text-white">Fernando Tech</strong> une os dois: somos <strong className="text-white">revenda e assistência técnica autorizada Daten</strong>,
              <strong className="text-white"> afiliado oficial Navegamer</strong> e temos nossa própria loja virtual — tudo com atendimento humano em Ribeirão Preto e entrega em todo o Brasil.
            </p>
            <p className="text-slate-400 leading-relaxed">
              Fale com a gente antes de comprar: indicamos a configuração ideal para a sua empresa, escola, escritório ou setup gamer, sem gastar além do necessário.
            </p>
          </div>

          <div className="lg:col-span-2 rounded-2xl bg-slate-900/70 border border-slate-800 p-6">
            <h3 className="text-white font-bold text-lg mb-1">Prefere que a gente te chame?</h3>
            <p className="text-slate-400 text-sm mb-5">Deixe seus dados e <strong className="text-cyan-300">a Fernando Tech entra em contato com você</strong> por telefone ou WhatsApp — você não precisa ligar.</p>
            <div className="flex flex-col gap-3">
              <CallbackButton className="w-full justify-center" testId="lead-callback-open" />
              <a href={WA} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all hover:scale-[1.02]" data-testid="lead-whatsapp">
                <MessageCircle className="w-4 h-4" /> Chamar no WhatsApp
              </a>
              <a href={SAC_PHONE_TEL} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-sm transition-colors" data-testid="lead-phone">
                <Phone className="w-4 h-4 text-cyan-400" /> SAC {SAC_PHONE_DISPLAY} · Opção 1
              </a>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-14">
          {OPTIONS.map(({ to, href, icon: Icon, title, tag, desc, color }) => {
            const c = COLORS[color];
            const inner = (
              <>
                <span className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border ${c.bg} mb-3`}><Icon className={`w-5 h-5 ${c.ic}`} /></span>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{tag}</p>
                <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{desc}</p>
                <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${c.ic}`}>Ver produtos <ArrowRight className="w-4 h-4" /></span>
              </>
            );
            const cls = `group block rounded-2xl bg-slate-900/70 border border-slate-800 ${c.border} p-6 transition-all hover:-translate-y-1`;
            const tid = `lead-option-${title.toLowerCase().replace(/\s+/g, '-')}`;
            return to
              ? <Link key={title} to={to} className={cls} data-testid={tid}>{inner}</Link>
              : <a key={title} href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={tid}>{inner}</a>;
          })}
        </div>

        <div className="max-w-3xl">
          <h3 className="flex items-center gap-2 text-white font-bold text-xl mb-4"><HelpCircle className="w-5 h-5 text-cyan-400" /> Perguntas frequentes sobre comprar com a Fernando Tech</h3>
          <div className="space-y-2">
            {FAQ.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={i} className={`rounded-xl border ${isOpen ? 'bg-slate-900 border-cyan-500/40' : 'bg-slate-900/50 border-slate-800'}`} data-testid={`lead-faq-${i}`}>
                  <button onClick={() => setOpen(isOpen ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left" aria-expanded={isOpen}>
                    <span className="text-white font-semibold text-sm">{f.q}</span>
                    <ChevronDown className={`w-4 h-4 text-cyan-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}>
                    <div className="overflow-hidden"><p className="px-4 pb-4 text-slate-400 text-sm leading-relaxed">{f.a}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadCapture;
